import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EMAIL_CONTATO, INSTAGRAM_URL } from '../config';
import VoltarTopo from '../components/VoltarTopo';
import Contador from '../components/Contador';
import Icone, { IconePlayStore, IconeInstagram } from '../components/Icones';
import Logo from '../components/Logo';
import { useScrollReveal } from '../lib/useReveal';
import { ID_GA4, ID_GTM_LANDING, installGoogleTag, installGtm } from '../lib/gtm';

const JORNADA: [string, string, string][] = [
  ['prancheta', 'Registra', 'Chamado com unidade, local, prioridade, fotos e responsável.'],
  ['calendario', 'Agenda', 'Atribuição por responsável, data, hora e histórico desde a abertura.'],
  ['checklist', 'Executa', 'Status, checklist, fotos, itens usados e tempo automático.'],
  ['grafico', 'Acompanha', 'Visão da operação por unidade, equipe, status e tempo médio.'],
];

const SIMPLES: [string, string, string][] = [
  ['chave', 'OS sem atrito', 'Chamados, status, histórico e evidências em poucos cliques.'],
  ['tecnico', 'Equipe e permissões', 'Gestor, supervisor, técnico e solicitante com acesso certo.'],
  ['chat', 'WhatsApp integrado', 'Criação, envio e assinatura da OS pelo próprio WhatsApp.'],
  ['prancheta', 'Relatórios em PDF', 'Gere e compartilhe relatórios e comprovantes da operação.'],
];

const PREMIUM: [string, string, string][] = [
  ['local', 'Rastreamento em tempo real', 'Acompanhe onde cada equipe está, em tempo real, direto no mapa.'],
  ['engrenagem', 'Automação de processos', 'Regras e fluxos automáticos que reduzem trabalho manual.'],
  ['caixa', 'Notas fiscais e estoque', 'Emissão de notas fiscais e controle de materiais vinculado à operação.'],
  ['qr', 'Ativos com QR Code', 'Ficha do ativo, fotos e histórico completo.'],
  ['video', 'Vídeos e imagens na OS', 'Anexe vídeos e fotos como prova do serviço executado.'],
  ['grafico', 'Relatórios e indicadores', 'Dashboard com produtividade, volume e tempo médio.'],
];

const CLIENTES: [string, string][] = [
  ['clientes/domus.webp', 'Domus Instalações Industriais'],
  ['clientes/ga-facilities.png', 'GA Facilities'],
  ['clientes/jocaru.png', 'Jocaru'],
  ['clientes/rras-industrial.webp', 'RRAS Industrial'],
  ['clientes/mjm-solucoes.png', 'MJM Soluções'],
];

const SEGMENTOS_HOME: [string, string][] = [
  ['cadeado', 'Segurança eletrônica'],
  ['caixa', 'Supermercados e PDV'],
  ['checklist', 'Limpeza'],
  ['predio', 'Condomínios e prédios'],
  ['chave', 'Manutenção'],
  ['tecnico', 'Prestadores de serviço'],
];

const DIFERENCIAIS: [string, string, string][] = [
  ['local', 'Rastreamento de equipe em tempo real', 'Veja no mapa onde cada colaborador está durante o dia. Direcione o chamado para quem está mais perto e comprove o deslocamento até a unidade.'],
  ['video', 'Vídeos e imagens dentro da OS', 'A equipe grava vídeos e anexa fotos direto do celular. A evidência fica na ordem de serviço e vai no relatório que chega ao cliente.'],
  ['chat', 'Assinatura da OS por WhatsApp', 'O cliente assina a ordem de serviço pelo próprio WhatsApp, sem app extra e sem papel. A confirmação fica registrada na OS.'],
];

const INTEGRATION_PONTOS: [string, string][] = [
  ['chat', 'Criação e compartilhamento de OS via WhatsApp'],
  ['raio', 'Menos retrabalho manual'],
  ['relogio', 'Alertas automáticos para a equipe'],
  ['grafico', 'Dados centralizados para decisão'],
];

const INTEGRACOES: [string, string, string, string][] = [
  ['whatsapp', 'WhatsApp', 'Atendimento', 'Criação de OS, envio para a equipe, compartilhamento com o cliente e assinatura da OS pelo próprio WhatsApp.'],
  ['pipedrive', 'Pipedrive', 'CRM', 'Clientes, oportunidades e solicitações comerciais conectadas ao atendimento.'],
  ['notafiscal', 'Nota Fiscal', 'Financeiro', 'Emissão e vínculo de notas fiscais direto na ordem de serviço.'],
  ['gmail', 'Gmail', 'Comunicação', 'Envio de orçamentos, relatórios e avisos de OS direto por e-mail.'],
  ['pagseguro', 'PagSeguro', 'Pagamentos', 'Cobrança e recebimento de pagamentos de OS e assinaturas.'],
  ['getnet', 'GetNet', 'Pagamentos', 'Conciliação de recebíveis e transações de cartão da operação.'],
  ['omini', 'Omini', 'Omnichannel', 'Canais de atendimento integrados para centralizar a operação do cliente.'],
];

// Passo a passo por categoria (em vez de uma grade única): preserva a ordem de
// primeira aparição de cada "tipo" em INTEGRACOES.
const INTEGRACOES_POR_GRUPO: [string, [string, string, string, string][]][] = INTEGRACOES.reduce(
  (acc: [string, [string, string, string, string][]][], item) => {
    const grupo = item[2] || 'Outros';
    const existente = acc.find(([g]) => g === grupo);
    if (existente) existente[1].push(item);
    else acc.push([grupo, [item]]);
    return acc;
  },
  [],
);

const PORQUES: [string, string, string][] = [
  ['raio', 'Implantação rápida', 'A equipe começa pelo básico e evolui sem travar a operação.'],
  ['checklist', 'Execução padronizada', 'Checklists, fotos e histórico reduzem retrabalho e esquecimento.'],
  ['grafico', 'Gestão com dados', 'Dashboard mostra volume, tempo médio, responsáveis e gargalos.'],
  ['usuario', 'Permissões por perfil', 'Gestor, supervisor, técnico e solicitante acessam só o que precisam.'],
];

const DORES: [string, string][] = [
  ['Chamado perdido no WhatsApp', 'Fila única de OS com status e responsável.'],
  ['Equipe sem roteiro', 'Checklist certo pela categoria do chamado.'],
  ['Sem prova do serviço', 'Fotos, histórico e PDF da OS.'],
  ['Estoque sem controle', 'Baixa de material vinculada ao chamado.'],
];

const DEPOIMENTOS: [string, string, string, string, string][] = [
  ['Reduzimos o tempo de resposta dos chamados pela metade. A equipe aprendeu a usar no mesmo dia.', 'Marina Alves', 'Gerente de Operações', 'MA', '-50% tempo de resposta'],
  ['A tela de OS ficou simples para a equipe e completa para o gestor. Era o equilíbrio que faltava.', 'Rodrigo Lima', 'Coordenador de Equipe', 'RL', 'Adoção em 1 dia'],
  ['O dashboard tirou nossa operação da planilha. Agora eu sei onde está o gargalo.', 'Patricia Souza', 'Diretora de Operações', 'PS', '100% visibilidade'],
];

type LandingSection = 'fluxo' | 'porque' | 'modulos' | 'integracoes' | 'planos';

function scrollLanding(id: LandingSection) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function LaptopMock() {
  return (
    <div className="laptop-mock" aria-hidden="true">
      <div className="laptop-screen">
        <div className="laptop-cam" />
        <img src="/aba-relatorio.png" alt="" />
      </div>
      <div className="laptop-base"><i /></div>
    </div>
  );
}

function PhoneAppMock() {
  return (
    <div className="phone-app-mock" aria-hidden="true">
      <div className="phone-app-frame phone-app-frame-2">
        <div className="phone-app-cam" />
        <img className="phone-app-shot" src={`${process.env.PUBLIC_URL}/app-screens/notificacao-app.jpeg`} alt="" />
      </div>
      <div className="phone-app-frame phone-app-frame-1">
        <div className="phone-app-cam" />
        <img className="phone-app-shot" src={`${process.env.PUBLIC_URL}/app-screens/tela-app.jpeg`} alt="" />
      </div>
    </div>
  );
}

function PlatformMark({ slug, title }: { slug: string; title: string }) {
  if (slug === 'whatsapp') {
    return (
      <div className="platform-mark platform-whatsapp" aria-hidden="true">
        <Icone nome="chat" size={30} />
      </div>
    );
  }
  if (slug === 'gmail') {
    return (
      <div className="platform-mark platform-gmail" aria-hidden="true">
        <Icone nome="email" size={28} />
      </div>
    );
  }
  if (slug === 'notafiscal') {
    return (
      <div className="platform-mark platform-notafiscal" aria-hidden="true">
        <Icone nome="prancheta" size={28} />
      </div>
    );
  }
  if (slug === 'pagseguro' || slug === 'getnet') {
    return (
      <div className={'platform-mark platform-' + slug} aria-hidden="true">
        <Icone nome="cartao" size={28} />
      </div>
    );
  }
  return (
    <div className={'platform-mark platform-' + slug} aria-hidden="true">
      <span>{title.charAt(0)}</span>
    </div>
  );
}

export default function Landing({ initialSection }: { initialSection?: LandingSection } = {}) {
  useScrollReveal();
  // Google Tag Manager — escopado às rotas de landing do Manux Service.
  // Não vai no public/index.html para não carregar no app logado (/app/*).
  useEffect(() => {
    installGtm(ID_GTM_LANDING);
    installGoogleTag(ID_GA4);
  }, []);
  useEffect(() => {
    if (!initialSection) return;
    const timer = window.setTimeout(() => scrollLanding(initialSection), 80);
    return () => window.clearTimeout(timer);
  }, [initialSection]);

  function irPara(id: LandingSection) {
    if (window.location.hash !== '#/') window.history.replaceState(null, '', '#/');
    window.setTimeout(() => scrollLanding(id), 0);
  }

  return (
    <div className="lp-page">
      <nav className="lp-nav lp-nav-v2">
        <Link to="/"><Logo /></Link>
        <span className="lp-nav-links">
          <button type="button" className="lp-link" onClick={() => irPara('integracoes')}>Integrações</button>
          <button type="button" className="lp-link" onClick={() => irPara('porque')}>Por que usar</button>
          <button type="button" className="lp-link" onClick={() => irPara('fluxo')}>Fluxo</button>
          <button type="button" className="lp-link" onClick={() => irPara('modulos')}>Módulos</button>
          <button type="button" className="lp-link" onClick={() => irPara('planos')}>Planos</button>
          <Link to="/login" className="btn sec sm">Entrar</Link>
          <Link to="/cadastro" className="btn sm">Criar conta</Link>
        </span>
      </nav>

      <header className="lp-hero-v2">
        <div className="lp-hero-inner">
          <div className="lp-hero-copy surge">
            <h1>Manux Service</h1>
            <div className="lp-cta">
              <Link to="/cadastro" className="btn lg">Criar conta grátis</Link>
              <button type="button" className="btn lg ghost" onClick={() => irPara('fluxo')}>Ver como funciona</button>
            </div>
            <a href="/app" target="_blank" rel="noopener noreferrer" className="btn lg appdl-btn hero-play-btn">
              <IconePlayStore size={26} />
              <span><i>Disponível no</i><b>Google Play</b></span>
            </a>
            <div className="lp-proof">
              <div><b><Contador alvo={10} sufixo="min" /></b><span>para abrir a primeira OS</span></div>
              <div><b>Web + app</b><span>a equipe usa em campo</span></div>
              <div><b><Contador alvo={2000} sufixo="+" /></b><span>OS por mês na plataforma</span></div>
            </div>
            <button type="button" className="hero-integrations" onClick={() => irPara('integracoes')}>
              <span>Integra com</span>
              <b>WhatsApp</b>
              <b>Pipedrive</b><b>Nota Fiscal</b><b>PagSeguro</b><b>GetNet</b><b>Omini</b>
            </button>
          </div>
          <div className="hero-devices">
            <LaptopMock />
            <div className="hero-devices-phone">
              <PhoneAppMock />
            </div>
          </div>
        </div>
      </header>

      <section className="lp-clientes">
        <div className="lp-clientes-head reveal">
          <span>Empresas que confiam na gente</span>
          <h2>Quem já roda a operação no <b>Manux Service</b></h2>
        </div>
        <div className="lp-clientes-logos">
          {CLIENTES.map(([arquivo, nome], i) => (
            <div className="cliente-logo reveal" key={nome} style={{ transitionDelay: `${i * 0.06}s` }}>
              <img src={`${process.env.PUBLIC_URL}/${arquivo}`} alt={nome} loading="lazy" />
              <span>{nome}</span>
            </div>
          ))}
        </div>
        <div className="lp-proof" style={{ maxWidth: 720, margin: '40px auto 0' }}>
          <div className="reveal"><b><Contador alvo={2000} sufixo="+" /></b><span>OS por mês na plataforma</span></div>
          <div className="reveal" style={{ transitionDelay: '.08s' }}><b><Contador alvo={10} sufixo=" mil+" /></b><span>transações processadas</span></div>
          <div className="reveal" style={{ transitionDelay: '.16s' }}><b><Contador alvo={300} sufixo="+" /></b><span>ativos gerenciados</span></div>
        </div>
      </section>

      <section className="lp-sec" id="segmentos">
        <div className="lp-section-head reveal">
          <span>Para quem é</span>
          <h2>Uma plataforma de gestão operacional para qualquer segmento</h2>
          <p>Ordens de serviço, ativos, documentos e automação — adaptado para a realidade de cada operação.</p>
        </div>
        <div className="seg-grid">
          {SEGMENTOS_HOME.map(([ic, nome], i) => (
            <div className="seg reveal" key={nome} style={{ transitionDelay: `${i * 0.05}s` }}>
              <div className="ic"><Icone nome={ic} size={30} /></div>
              <b>{nome}</b>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-sec lp-integrations lp-integrations-top" id="integracoes">
        <div className="lp-section-head reveal">
          <span>Integrações</span>
          <h2>A Manux entra no meio do seu fluxo, não por cima dele</h2>
          <p>Sua equipe continua usando o que já domina. A Manux vira o ponto único de controle das ordens de serviço e conversa com WhatsApp, e-mail, CRM, nota fiscal, pagamentos e canais de atendimento.</p>
          <div className="integration-points">
            {INTEGRATION_PONTOS.map(([ic, texto]) => (
              <b key={texto}><Icone nome={ic} size={18} /> {texto}</b>
            ))}
          </div>
          <button type="button" className="btn" onClick={() => irPara('planos')}>Ver planos integrados</button>
        </div>
        <div className="integrations-timeline">
          {INTEGRACOES_POR_GRUPO.map(([grupo, itens], gi) => (
            <div className="int-tl-grupo reveal" key={grupo} style={{ transitionDelay: `${gi * 0.08}s` }}>
              <div className="int-tl-marcador"><span /></div>
              <div className="int-tl-conteudo">
                <span className="int-tl-rotulo">{grupo}</span>
                <div className="int-tl-itens">
                  {itens.map(([slug, titulo, , texto]) => (
                    <div className="integration-item" key={titulo}>
                      <PlatformMark slug={slug} title={titulo} />
                      <strong>{titulo}</strong>
                      <p>{texto}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-strip reveal">
        {DORES.map(([antes, depois]) => (
          <div key={antes}>
            <span>{antes}</span>
            <b>{depois}</b>
          </div>
        ))}
      </section>

      <section className="lp-sec lp-diferencial">
        <div className="lp-section-head reveal">
          <span>Nosso diferencial</span>
          <h2>O que só a Manux Service entrega no campo</h2>
          <p>Enquanto os outros param no chamado, a gente prova onde a equipe esteve e o que foi feito — com vídeo, foto e localização na própria OS.</p>
        </div>
        <div className="dif-grid">
          {DIFERENCIAIS.map(([ic, titulo, texto], i) => (
            <div className="dif-card reveal" key={titulo} style={{ transitionDelay: `${i * 0.1}s` }}>
              <span className="dif-flag">Exclusivo</span>
              <div className="dif-ic"><Icone nome={ic} size={30} /></div>
              <h3>{titulo}</h3>
              <p>{texto}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-sec lp-why" id="porque">
        <div className="lp-section-head reveal">
          <span>Por que usar</span>
          <h2>O Manux Service organiza sua operação sem complicar sua equipe</h2>
          <p>O gestor vê o que está aberto e quem está resolvendo. A equipe executa e registra tudo pelo celular. Sem planilha paralela e sem chamado perdido no grupo do WhatsApp.</p>
        </div>
        <div className="why-grid">
          {PORQUES.map(([ic, titulo, texto], i) => (
            <div className="why-card reveal" key={titulo} style={{ transitionDelay: `${i * 0.07}s` }}>
              <Icone nome={ic} size={26} />
              <h3>{titulo}</h3>
              <p>{texto}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-sec lp-flow" id="fluxo">
        <div className="lp-section-head reveal">
          <span>Fluxo de trabalho</span>
          <h2>Da abertura ao fechamento sem perder contexto</h2>
          <p>O sistema acompanha a OS como ela acontece: local, equipe, evidências, tempo, itens usados e histórico.</p>
        </div>
        <div className="lp-flow-grid">
          {JORNADA.map(([ic, titulo, texto], i) => (
            <div className="lp-flow-step reveal" key={titulo} style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="step-index">{String(i + 1).padStart(2, '0')}</div>
              <Icone nome={ic} size={28} />
              <h3>{titulo}</h3>
              <p>{texto}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-modules" id="modulos">
        <div className="lp-sec">
          <div className="lp-section-head reveal">
            <span>Módulos</span>
            <h2>Simples para começar. Premium para controlar tudo.</h2>
            <p>A operação básica fica limpa, e os recursos avançados aparecem quando o plano pede.</p>
          </div>
          <div className="module-columns">
            <div className="module-column reveal">
              <div className="module-title"><b>Plano Simples</b><span>OS e equipe</span></div>
              {SIMPLES.map(([ic, titulo, texto]) => (
                <div className="module-item" key={titulo}>
                  <Icone nome={ic} size={22} />
                  <span><b>{titulo}</b><small>{texto}</small></span>
                </div>
              ))}
            </div>
            <div className="module-column premium reveal" style={{ transitionDelay: '.1s' }}>
              <div className="module-title"><b>Plano Premium</b><span>Rastreamento, mídia na OS e mais</span></div>
              {PREMIUM.map(([ic, titulo, texto]) => (
                <div className="module-item" key={titulo}>
                  <Icone nome={ic} size={22} />
                  <span><b>{titulo}</b><small>{texto}</small></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lp-sec lp-command">
        <div className="command-copy reveal">
          <span>Visão do gestor</span>
          <h2>Menos tela solta, mais decisão rápida.</h2>
          <p>Veja o que está aberto, quem está executando, quanto tempo levou e quais materiais foram usados.</p>
          <Link to="/cadastro?plano=premium" className="btn">Testar Premium</Link>
        </div>
        <div className="command-shot reveal">
          <div className="shot-frame">
            <div className="shot-bar"><i /><i /><i /></div>
            <img
              src={`${process.env.PUBLIC_URL}/dashboard_manux.svg`}
              alt="Dashboard do gestor no Manux Service: indicadores de OS, horas trabalhadas e distribuição de chamados"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="lp-depos-v2">
        <div className="lp-sec">
          <div className="lp-section-head reveal">
            <span>Resultados reais</span>
            <h2>Feito para equipes que precisam resolver, não decorar sistema.</h2>
            <p>Não são elogios genéricos — são números que gestores acompanham toda semana.</p>
          </div>
          <div className="depo-grid">
            {DEPOIMENTOS.map(([txt, nome, cargo, ini, resultado], i) => (
              <div className="depo reveal" key={nome} style={{ transitionDelay: `${i * 0.1}s` }}>
                <span className="depo-resultado">{resultado}</span>
                <span className="depo-aspas">“</span>
                <p>{txt}</p>
                <div className="quem"><div className="avatar">{ini}</div><div><b>{nome}</b><span>{cargo}</span></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-sec" id="planos">
        <div className="lp-section-head reveal">
          <span>Planos</span>
          <h2>Escolha pelo momento da operação</h2>
          <p>Sem travar seu time: comece com OS e evolua para ativos, checklist, estoque e dashboard.</p>
        </div>
        <div className="lp-oferta reveal">
          <b>Oferta de lançamento</b>
          <span>Por tempo limitado, comece no Simples por <strong>R$ 90/mês</strong> ou tenha automação, notas fiscais, ativos e indicadores no Premium por <strong>R$ 150/mês</strong>.</span>
        </div>
        <div className="lp-planos lp-planos-v2">
          <div className="lp-plano esmaecido reveal">
            <h3>Simples</h3>
            <div className="plan-price"><strong>R$ 90,00</strong><span>/mês</span></div>
            <p>Para controlar chamados e equipe.</p>
            <ul>
              <li>Ordens de serviço</li>
              <li>Unidades e locais</li>
              <li>Equipe e permissões</li>
              <li>Assinatura da OS por WhatsApp</li>
              <li>Relatórios em PDF</li>
              <li>Implementação sem cobrança</li>
              <li>Suporte prioritário</li>
            </ul>
            <Link to="/cadastro?plano=simples" className="btn sec" style={{ width: '100%' }}>Começar no Simples</Link>
          </div>
          <div className="lp-plano destaque reveal" style={{ transitionDelay: '.1s' }}>
            <span className="badge">Oferta de lançamento</span>
            <h3>Premium</h3>
            <div className="plan-price"><strong>R$ 150,00</strong><span>/mês</span></div>
            <p>Tudo do Simples com automação, notas fiscais e gestão de ativos.</p>
            <ul>
              <li>Rastreamento em tempo real</li>
              <li>Automação de processos</li>
              <li>Notas fiscais e estoque</li>
              <li>Ativos com QR Code</li>
              <li>Vídeos e imagens na OS</li>
              <li>Relatórios e indicadores avançados</li>
            </ul>
            <Link to="/cadastro?plano=premium" className="btn" style={{ width: '100%' }}>Aproveitar a oferta</Link>
          </div>
        </div>
      </section>

      <section className="lp-final-v2 reveal">
        <div>
          <span>Pronto para organizar sua operação?</span>
          <h2>Crie sua conta e libere a operação depois da aprovação.</h2>
        </div>
        <div className="lp-final-actions">
          <Link to="/cadastro" className="btn lg">Criar conta grátis</Link>
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-foot-grid">
          <div>
            <Logo variant="light" />
            <p>Gestão operacional e automação.<br />CNPJ 48.634.165/0001-41</p>
          </div>
          <div>
            <strong style={{ color: '#fff' }}>Produto</strong>
            <p>
              <button type="button" onClick={() => irPara('fluxo')}>Fluxo</button><br />
              <button type="button" onClick={() => irPara('porque')}>Por que usar</button><br />
              <button type="button" onClick={() => irPara('modulos')}>Módulos</button><br />
              <button type="button" onClick={() => irPara('integracoes')}>Integrações</button><br />
              <button type="button" onClick={() => irPara('planos')}>Planos</button><br />
              <Link to="/solucoes">Soluções</Link>
            </p>
          </div>
          <div>
            <strong style={{ color: '#fff' }}>Legal</strong>
            <p><Link to="/privacidade">Política de Privacidade</Link><br /><Link to="/termos">Termos de Uso</Link></p>
          </div>
          <div>
            <strong style={{ color: '#fff' }}>Contato</strong>
            <p>
              <a href={`mailto:${EMAIL_CONTATO}`}>{EMAIL_CONTATO}</a><br />
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="lp-foot-instagram">
                <IconeInstagram size={20} /> Instagram
              </a><br />
              <Link to="/login">Entrar</Link>
            </p>
          </div>
        </div>
        <div className="copy">(c) {new Date().getFullYear()} Manux Service - CNPJ 48.634.165/0001-41 - Todos os direitos reservados.</div>
      </footer>

      <a
        className="instagram-float"
        href={INSTAGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Abrir Instagram da Manux Service"
        title="Instagram"
      >
        <IconeInstagram size={30} />
      </a>
      <VoltarTopo />
    </div>
  );
}
